import { useCallback, useState } from 'react';
import FileSaver from 'file-saver';
import { readerRequests } from 'services';
import { useViewTableData, skeletonFactory, useHandleRequest, useAzureStorage, domHelper } from 'shared';
import { CONTAINERS } from 'pages/shared';

const requestsDummy = skeletonFactory.readerRequests();

export function useReaderRequestsPaginated() {
  const { getReadSasToken } = useAzureStorage(CONTAINERS.READER_REQUESTS);
  const {
    data: readerRequestsData,
    get: getReaderRequests,
    paging,
    sorting,
    filteredCells,
    setData,
    undoData
  } = useViewTableData({
    service: readerRequests,
    paginatedSkeleton: requestsDummy
  });
  const { request, errorInfo, setErrorInfo } = useHandleRequest();
  const [isCreating, setIsCreating] = useState(false);

  const createRequest = useCallback(
    async readerRequest => {
      try {
        return await request(async () => {
          setIsCreating(true);
          setData({ ...requestsDummy });
          const created = await readerRequests.create(readerRequest);
          await getReaderRequests(paging, sorting, filteredCells);
          return created.data;
        });
      } catch (error) {
        undoData();
      } finally {
        setIsCreating(false);
      }
    },
    [request, setData, undoData, filteredCells, paging, sorting, getReaderRequests]
  );

  const processResponse = useCallback(
    async readerRequestId => {
      try {
        return await request(async () => {
          setIsCreating(true);
          setData({ ...requestsDummy });
          const result = await readerRequests.process(readerRequestId);
          if (result) {
            await getReaderRequests(paging, sorting, filteredCells);
          }
          return result;
        });
      } catch (error) {
        undoData();
      } finally {
        setIsCreating(false);
      }
    },
    [request, setData, undoData, filteredCells, paging, sorting, getReaderRequests]
  );

  async function handleDownload([documentUrl]) {
    if (documentUrl) {
      const token = await getReadSasToken();
      const downloadUrl = documentUrl + '?' + token;
      domHelper.downloadFile(downloadUrl, documentUrl.replace(' ', '_'));
    }
  }

  const handleExport = useCallback(async () => {
    try {
      return await request(async () => {
        const exportedFile = await readerRequests.export(filteredCells);
        if (exportedFile) {
          FileSaver.saveAs(exportedFile, 'reader-requests.zip');
        }
        return true;
      });
    } catch {
      return false;
    }
  }, [request, filteredCells]);

  return {
    readerRequests: readerRequestsData,
    getReaderRequests,
    createRequest,
    processResponse,
    handleDownload,
    handleExport,
    errorInfo,
    setErrorInfo,
    isCreating
  };
}
